import { useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { useControls, button } from 'leva'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'

import { cfgGumball, avviaGiro, chiudiPremio, manopolaBloccata } from '../../config/gumball'
import { DEBUG_MODE } from '../../config/debug'
import { gumballStore, mirinoGumballStore, premioGumballStore, isUiOpen } from '../../store/uiStore'

// ============================================================
// LA MACCHINETTA DELLE PALLINE, costruita a pezzi semplici come quella
// della foto di riferimento: zoccolo cromato, corpo nero lucido, placca
// del meccanismo con la manopola, scivolo con lo sportellino, collare,
// globo di vetro pieno di palline e coperchio a cupola.
//
// COORDINATE LOCALI: origine al centro della base, appoggiata sul
// bancone; +Y in alto; la faccia con la manopola guarda +Z. Il gruppo
// viene poi girato (cfgGumball.giro) verso chi deve usarla.
//
// LEGGERA PER COSTRUZIONE (chiesto esplicitamente). A riposo sono SETTE
// chiamate di disegno: i pezzi cromati fermi sono UNA geometria sola
// (erano otto), la manopola un'altra, il corpo col suo buco nero un'altra,
// poi sportellino, palline, vetro e riflessi. E le palline chiuse dentro
// il mucchio, che non si vedono da nessun lato, non vengono disegnate.
//
// Tutto il movimento sta in un useFrame e nei ref, niente useState: una
// giocata cambia fase quattro volte, e solo quelle passano da React.
// ============================================================

const GRADI = Math.PI / 180
const R_PALLINA = 0.026

const COLORI = ['#e32b2b', '#1f5fd6', '#1f9e45', '#f7d31e', '#f5821f', '#f2f2ee']

// Profilo del globo (raggio, quota) dal basso in alto: un barile coi
// fianchi appena bombati, come nella foto. Aperto sopra e sotto, perche'
// li' ci sono il collare e il coperchio.
const PROFILO = [
  [0.135, 0.000], [0.170, 0.012], [0.192, 0.040], [0.203, 0.090],
  [0.206, 0.200], [0.203, 0.310], [0.192, 0.360], [0.170, 0.388], [0.135, 0.400],
]
const Y_GLOBO = 0.365

// Il percorso della pallina che esce, in coordinate LOCALI della macchinetta
const DENTRO_SCIVOLO = new THREE.Vector3(0, 0.1, 0.15)
const SUL_LABBRO = new THREE.Vector3(0, 0.103, 0.214)
// ...e dove si ferma davanti agli occhi, in coordinate della CAMERA.
// Un filo SOPRA il centro. E NON TROPPO GRANDE: la lente a barile di
// Effects.jsx ingrandisce il centro dello schermo (zoom 1.44), quindi
// quello che ci sta davanti appare molto piu' grosso di quanto dica il
// conto della prospettiva. A scala 2.4 e distanza 0.45 copriva tutta la
// macchinetta.
const DAVANTI = new THREE.Vector3(0, 0.05, -0.5)
const SCALA_DAVANTI = 1.7

// IL BIGLIETTO DEL PREMIO: un foglietto di carta che sta DENTRO la
// pallina e si vede nella fessura quando le due meta' si aprono. E' piu'
// largo della pallina apposta, sporge dai lati come un biglietto della
// lotteria infilato in una capsula. Misure in unita' della pallina.
const BIGLIETTO_L = R_PALLINA * 3.4
const BIGLIETTO_A = R_PALLINA * 1.15
const TELA_L = 768
const TELA_A = Math.round(TELA_L * BIGLIETTO_A / BIGLIETTO_L)

// LA MANOPOLA GIRA A SCATTI, quattro quarti di giro: ogni quarto e' una
// rotazione svelta seguita da una pausa, come il cricchetto di una
// macchinetta vera. Una rotazione liscia sembrerebbe un motore.
const T_SCATTO = 0.3
const T_ROTAZ = 0.17
const T_CODA = 0.1   // la pausa dopo l'ultimo scatto, prima che la pallina esca

// Aperta la pallina, un click chiude — ma non subito: senza questa pausa
// un click partito durante il volo la chiuderebbe prima di leggerla.
const T_PRIMA_DI_CHIUDERE = 0.45

const easeOut = (x) => 1 - (1 - x) ** 3
const easeInOut = (x) => (x < 0.5 ? 4 * x * x * x : 1 - (-2 * x + 2) ** 3 / 2)
const clamp = THREE.MathUtils.clamp

function raggioProfilo(y) {
  for (let i = 1; i < PROFILO.length; i++) {
    const [r1, y1] = PROFILO[i]
    if (y <= y1) {
      const [r0, y0] = PROFILO[i - 1]
      return r0 + (r1 - r0) * ((y - y0) / (y1 - y0))
    }
  }
  return PROFILO[PROFILO.length - 1][0]
}

// Le palline del globo: strati a nido d'ape (ogni strato sfalsato sui
// buchi di quello sotto, come si sistemano davvero), tagliati dal profilo
// del vetro, con la superficie a cupoletta — un mucchio, non un livello
// d'acqua. Un filo di disordine perche' un reticolo perfetto si vede.
// SI TENGONO SOLO QUELLE CHE SI POSSONO VEDERE: il guscio contro il vetro
// e lo strato di cima (da in piedi il mucchio lo si guarda anche da
// sopra). Quelle chiuse dentro sono coperte da tutti i lati.
const CIMA = (rho) => 0.29 - 0.9 * rho * rho
function riempiGlobo() {
  const r = R_PALLINA
  const passo = 2 * r * 1.03
  const passoY = 2 * r * 0.84
  const posti = []
  let strato = 0
  for (let y = r + 0.002; y < 0.3; y += passoY, strato++) {
    const sfx = (strato % 2) * passo * 0.5
    const sfz = (strato % 2) * passo * 0.289
    const massimo = raggioProfilo(y) - r - 0.007
    for (let j = -9; j <= 9; j++) {
      for (let i = -9; i <= 9; i++) {
        const x = (i + (Math.abs(j) % 2) * 0.5) * passo + sfx
        const z = j * passo * 0.866 + sfz
        const rho = Math.hypot(x, z)
        if (rho > massimo || y > CIMA(rho)) continue
        const esterna = rho > massimo - 1.7 * r
        const inCima = y + passoY > CIMA(rho)
        if (!esterna && !inCima) continue
        const d = () => (Math.random() - 0.5) * r * 0.25
        posti.push({ x: x + d(), y: y + d() * 0.5, z: z + d(), esterna })
      }
    }
  }
  return posti
}

// Porta un pezzo al suo posto e lo rende compatibile con gli altri per la
// fusione: tutti non indicizzati, tutti con posizione, normale e uv.
function pezzo(g, { pos = [0, 0, 0], rotX = 0, scala = null } = {}) {
  const n = g.index ? g.toNonIndexed() : g
  if (rotX) n.rotateX(rotX)
  if (scala) n.scale(...scala)
  n.translate(...pos)
  return n
}

function rettangoloTondo(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

// Il biglietto: carta chiara, due bande del colore del premio alle
// estremita' (con la linea tratteggiata della perforazione), e il premio
// scritto in mezzo. Si ridisegna UNA volta per giocata, quando il premio
// viene pescato — mai per fotogramma.
function disegnaBiglietto(ctx, premio) {
  const W = TELA_L
  const H = TELA_A
  ctx.clearRect(0, 0, W, H)
  ctx.save()
  rettangoloTondo(ctx, 3, 3, W - 6, H - 6, H * 0.16)
  ctx.fillStyle = '#fffdf5'
  ctx.fill()
  ctx.clip()
  // una pallina bianca ("ritenta") darebbe bande invisibili sulla carta
  const colore = new THREE.Color(premio.colore)
  const chiaro = colore.r * 0.3 + colore.g * 0.59 + colore.b * 0.11 > 0.85
  ctx.fillStyle = chiaro ? '#cfcfc6' : premio.colore
  const banda = W * 0.055
  ctx.fillRect(0, 0, banda, H)
  ctx.fillRect(W - banda, 0, banda, H)
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)'
  ctx.lineWidth = 3
  ctx.setLineDash([9, 8])
  ctx.beginPath()
  ctx.moveTo(banda + 10, 8); ctx.lineTo(banda + 10, H - 8)
  ctx.moveTo(W - banda - 10, 8); ctx.lineTo(W - banda - 10, H - 8)
  ctx.stroke()
  ctx.restore()

  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillStyle = premio.oro ? '#9a6a00' : '#1c1c20'
  ctx.font = `bold ${Math.round(H * 0.36)}px Arial, Helvetica, sans-serif`
  ctx.fillText(premio.titolo, W / 2, H * 0.41, W * 0.76)
  // col codice la seconda riga e' la cosa che conta: piu' grande e scura
  ctx.fillStyle = premio.codice ? '#2c2c32' : '#5c5c62'
  ctx.font = `${premio.codice ? 'bold ' : ''}${Math.round(H * (premio.codice ? 0.2 : 0.17))}px Arial, Helvetica, sans-serif`
  ctx.fillText(premio.testo, W / 2, H * 0.75, W * 0.76)
}

const tmp = {
  cam: new THREE.Vector3(), dir: new THREE.Vector3(), centro: new THREE.Vector3(),
  verso: new THREE.Vector3(), loc: new THREE.Vector3(), a: new THREE.Vector3(),
  b: new THREE.Vector3(), q: new THREE.Quaternion(), m: new THREE.Matrix4(),
  c: new THREE.Color(),
}

export default function Gumball() {
  const macchina = useRef()
  const manopola = useRef()
  const sportello = useRef()
  const palline = useRef()
  const mucchio = useRef()
  const uscita = useRef()
  const metaSu = useRef()
  const metaGiu = useRef()
  const biglietto = useRef()
  const camera = useThree((s) => s.camera)
  const gl = useThree((s) => s.gl)

  const cfg = useControls('Macchinetta', {
    x: { value: cfgGumball.x, min: 1.6, max: 4, step: 0.01 },
    y: { value: cfgGumball.y, min: 1.5, max: 2.5, step: 0.005 },
    z: { value: cfgGumball.z, min: -4.8, max: 0.8, step: 0.01 },
    giro: { value: cfgGumball.giro, min: -180, max: 180, step: 1 },
    scala: { value: cfgGumball.scala, min: 0.5, max: 2, step: 0.01 },
    portata: { value: cfgGumball.portata, min: 1, max: 6, step: 0.1 },
    cono: { value: cfgGumball.cono, min: 4, max: 40, step: 1, label: 'cono di mira (°)' },
    'gira la manopola': button(() => avviaGiro()),
  }, { collapsed: true })
  Object.assign(cfgGumball, cfg)

  // ---------- materiali ----------
  const mat = useMemo(() => ({
    cromo: new THREE.MeshStandardMaterial({ color: '#dfe3e8', metalness: 1, roughness: 0.16 }),
    nero: new THREE.MeshStandardMaterial({ color: '#0d0d0f', metalness: 0.3, roughness: 0.28 }),
    // IL VETRO NON USA LA TRASMISSIONE: costerebbe una seconda resa
    // dell'intero negozio a ogni fotogramma solo per questo globo. E' un
    // velo trasparente che prende i riflessi dell'ambiente, piu' le strisce
    // di luce disegnate. Una faccia sola: la parete di dietro, a opacita'
    // 0.14, non si distingueva, e a due facce three la disegna due volte.
    vetro: new THREE.MeshPhysicalMaterial({
      color: '#ffffff', transparent: true, opacity: 0.14, roughness: 0.03,
      metalness: 0, clearcoat: 1, clearcoatRoughness: 0.02,
      envMapIntensity: 1.6, side: THREE.FrontSide, depthWrite: false,
    }),
    riflesso: new THREE.MeshBasicMaterial({
      color: '#ffffff', transparent: true, opacity: 0.26,
      blending: THREE.AdditiveBlending, depthWrite: false,
    }),
    pallina: new THREE.MeshStandardMaterial({ roughness: 0.28, metalness: 0 }),
    fuori: new THREE.MeshStandardMaterial({ color: '#ffffff', roughness: 0.28, side: THREE.FrontSide }),
    dentro: new THREE.MeshStandardMaterial({ color: '#fff4dc', roughness: 0.6, side: THREE.BackSide }),
  }), [])

  // ---------- il biglietto: una tela e una texture, riusate a ogni giocata ----------
  const carta = useMemo(() => {
    const tela = document.createElement('canvas')
    tela.width = TELA_L
    tela.height = TELA_A
    const tex = new THREE.CanvasTexture(tela)
    tex.colorSpace = THREE.SRGBColorSpace
    const materiale = new THREE.MeshBasicMaterial({ map: tex, transparent: true, toneMapped: false })
    return { ctx: tela.getContext('2d'), tex, materiale }
  }, [])

  // ---------- geometrie ----------
  const geo = useMemo(() => {
    const punti = PROFILO.map(([r, y]) => new THREE.Vector2(r, y))
    const puntiFuori = PROFILO.map(([r, y]) => new THREE.Vector2(r * 1.006, y))
    return {
      // tutti i pezzi cromati che non si muovono, in UNA geometria
      cromoFisso: mergeGeometries([
        pezzo(new THREE.BoxGeometry(0.36, 0.03, 0.34), { pos: [0, 0.015, 0] }),              // zoccolo
        pezzo(new THREE.BoxGeometry(0.2, 0.16, 0.02), { pos: [0, 0.245, 0.16] }),            // placca
        pezzo(new THREE.BoxGeometry(0.13, 0.08, 0.045), { pos: [0, 0.105, 0.172] }),         // scivolo
        pezzo(new THREE.BoxGeometry(0.13, 0.012, 0.04), { pos: [0, 0.071, 0.21] }),          // labbro
        pezzo(new THREE.CylinderGeometry(0.168, 0.172, 0.035, 40), { pos: [0, 0.3475, 0] }), // collare
        pezzo(new THREE.CylinderGeometry(0.188, 0.192, 0.03, 40), { pos: [0, 0.78, 0] }),    // anello
        pezzo(new THREE.SphereGeometry(0.175, 32, 8, 0, Math.PI * 2, 0, Math.PI / 2),
          { pos: [0, 0.795, 0], scala: [1, 0.32, 1] }),                                      // cupola
        pezzo(new THREE.CylinderGeometry(0.026, 0.03, 0.022, 16), { pos: [0, 0.862, 0] }),   // pomolo
      ]),
      // la manopola: disco e presa, girano insieme
      manopola: mergeGeometries([
        pezzo(new THREE.CylinderGeometry(0.066, 0.066, 0.018, 32), { pos: [0, 0, 0.009], rotX: Math.PI / 2 }),
        pezzo(new RoundedBoxGeometry(0.105, 0.03, 0.03, 2, 0.012), { pos: [0, 0, 0.033] }),
      ]),
      // il corpo nero, col buco dello scivolo dello stesso nero: un
      // materiale in meno e una chiamata in meno, e un buco lucido si
      // legge come buio lo stesso
      corpo: mergeGeometries([
        pezzo(new RoundedBoxGeometry(0.32, 0.3, 0.3, 2, 0.02), { pos: [0, 0.18, 0] }),
        pezzo(new THREE.PlaneGeometry(0.092, 0.052), { pos: [0, 0.1, 0.1955] }),
      ]),
      sportello: new THREE.BoxGeometry(0.098, 0.052, 0.005),
      globo: new THREE.LatheGeometry(punti, 48),
      // le due strisce di luce sul vetro (phi 0 = +Z, la faccia della
      // manopola), fuse: una chiamata sola
      riflessi: mergeGeometries([
        new THREE.LatheGeometry(puntiFuori, 6, -0.62, 0.16),
        new THREE.LatheGeometry(puntiFuori, 4, 0.34, 0.06),
      ]),
      // 12x8 spicchi: da in piedi davanti al bancone una pallina fa una
      // quindicina di pixel, e di piu' non si vede
      pallina: new THREE.SphereGeometry(R_PALLINA, 12, 8),
      su: new THREE.SphereGeometry(R_PALLINA, 20, 10, 0, Math.PI * 2, 0, Math.PI / 2),
      giu: new THREE.SphereGeometry(R_PALLINA, 20, 10, 0, Math.PI * 2, Math.PI / 2, Math.PI / 2),
      biglietto: new THREE.PlaneGeometry(BIGLIETTO_L, BIGLIETTO_A),
    }
  }, [])

  const posti = useMemo(riempiGlobo, [])

  useLayoutEffect(() => {
    const im = palline.current
    if (!im) return
    // NIENTE PALLINE D'ORO: c'erano quando fra i premi c'era il 20%, e
    // mostrare nel vetro un premio che non si puo' vincere sarebbe una
    // promessa falsa. Se torna un premio grosso (oro: true nella tabella di
    // api/gumball.js, che la pallina e il biglietto sanno gia' disegnare),
    // tornino anche loro — sono nella storia git.
    posti.forEach((p, i) => {
      tmp.m.makeTranslation(p.x, p.y, p.z)
      im.setMatrixAt(i, tmp.m)
      tmp.c.set(COLORI[Math.floor(Math.random() * COLORI.length)])
      im.setColorAt(i, tmp.c)
    })
    im.instanceMatrix.needsUpdate = true
    if (im.instanceColor) im.instanceColor.needsUpdate = true
    im.computeBoundingSphere()
  }, [posti])

  // ---------- orologio delle fasi ----------
  // L'istante d'inizio fase si prende AL CAMBIO DI STATO, non nel
  // useFrame: se il fotogramma dopo arriva tardi, la fase partirebbe in
  // ritardo e le durate si sfaserebbero (lezione dell'orologio da polso).
  const fase = useRef({ nome: gumballStore.get(), da: performance.now(), scatti: 0, fine: 4, scossa: 0 })
  useEffect(() => gumballStore.subscribe(() => {
    const n = gumballStore.get()
    const f = fase.current
    if (n === f.nome) return
    f.nome = n
    f.da = performance.now()
    f.scatti = 0
    f.fine = 4
  }), [])

  // IL PREMIO ARRIVA DAL SERVER mentre la manopola gira (o subito, se e'
  // una giocata di prova): la pallina prende il suo colore e il biglietto
  // si scrive adesso, una volta sola.
  useEffect(() => premioGumballStore.subscribe(() => {
    const p = premioGumballStore.get()
    if (!p) return
    mat.fuori.color.set(p.colore || '#ffffff')
    mat.fuori.metalness = p.oro ? 0.85 : 0
    mat.fuori.roughness = p.oro ? 0.22 : 0.28
    disegnaBiglietto(carta.ctx, p)
    carta.tex.needsUpdate = true
  }), [mat, carta])

  // Aperta la pallina, un click (o un tocco) la chiude. Il mouse resta
  // agganciato per tutta la giocata: il premio non ha piu' un pannello da
  // cliccare, quindi non serve liberarlo — e senza sganciare non c'e'
  // nemmeno un riaggancio da proteggere.
  useEffect(() => {
    const tela = gl.domElement
    const onGiu = () => {
      const f = fase.current
      if (f.nome !== 'apre') return
      if ((performance.now() - f.da) / 1000 < T_PRIMA_DI_CHIUDERE) return
      chiudiPremio()
    }
    tela.addEventListener('pointerdown', onGiu)
    return () => tela.removeEventListener('pointerdown', onGiu)
  }, [gl])

  useEffect(() => {
    if (!DEBUG_MODE) return undefined
    window.__gumball = {
      gira: avviaGiro, fase: () => gumballStore.get(), palline: posti.length,
      macchina: () => macchina.current, istanze: () => palline.current,
    }
    return () => { delete window.__gumball }
  }, [posti])

  useFrame((state, delta) => {
    const f = fase.current
    const t = (performance.now() - f.da) / 1000
    const m = macchina.current
    const u = uscita.current
    if (!m || !u) return

    // --- MIRA: e' nel mirino se e' vicina e quasi al centro della vista ---
    if (f.nome === 'ferma') {
      camera.getWorldPosition(tmp.cam)
      camera.getWorldDirection(tmp.dir)
      m.localToWorld(tmp.centro.set(0, 0.42, 0))
      tmp.verso.subVectors(tmp.centro, tmp.cam)
      const dist = tmp.verso.length()
      const dentro = !isUiOpen() && dist < cfgGumball.portata &&
        tmp.dir.dot(tmp.verso.divideScalar(dist)) > Math.cos(cfgGumball.cono * GRADI)
      // lo store si scrive SOLO al cambio: il set avvisa gli iscritti anche
      // a valore uguale, e sarebbe un render di React per fotogramma
      if (dentro !== mirinoGumballStore.get()) mirinoGumballStore.set(dentro)
    } else if (mirinoGumballStore.get()) {
      mirinoGumballStore.set(false)
    }

    // --- IL MUCCHIO SUSSULTA a ogni scatto della manopola ---
    // ATTENZIONE: la quota del mucchio E' Y_GLOBO, e lo scossone le si
    // SOMMA. La prima stesura scriveva y = -0.0025·scossa, cioe' 0 appena
    // lo scossone si spegneva: il mucchio cadeva dentro il corpo nero e le
    // palline spuntavano dal davanti, all'altezza dello scivolo (visto).
    // A riposo non si tocca nemmeno: niente scossone, niente scrittura.
    if (f.scossa > 0.001) {
      f.scossa *= Math.exp(-delta * 8)
      mucchio.current.position.x = Math.sin(performance.now() * 0.06) * 0.0035 * f.scossa
      mucchio.current.position.y = Y_GLOBO - 0.0025 * f.scossa
    }

    const scala = cfgGumball.scala

    if (f.nome === 'gira') {
      // QUATTRO SCATTI, e se il server non ha ancora risposto altri DUE alla
      // volta finche' non risponde: sempre in coppia, cosi' la presa finisce
      // orizzontale (e' simmetrica per mezzo giro) e tornare a zero non si vede.
      const pronto = premioGumballStore.get() !== null
      if (!pronto && t >= f.fine * T_SCATTO) f.fine += 2
      const passo = Math.min(Math.floor(t / T_SCATTO), f.fine)
      const k = passo >= f.fine ? 0 : easeOut(Math.min((t - passo * T_SCATTO) / T_ROTAZ, 1))
      manopola.current.rotation.z = -(passo + k) * Math.PI / 2  // senso orario visto da davanti
      if (passo > f.scatti) { f.scatti = passo; f.scossa = 1 }
      if (pronto && t >= f.fine * T_SCATTO + T_CODA) gumballStore.set('esce')
      return
    }

    if (f.nome === 'esce') {
      sportello.current.rotation.x = -easeOut(Math.min(t / 0.16, 1)) * 1.05
      const k = clamp((t - 0.1) / 0.45, 0, 1)
      tmp.loc.lerpVectors(DENTRO_SCIVOLO, SUL_LABBRO, easeOut(k))
      tmp.loc.y += Math.sin(k * Math.PI) * 0.012
      m.localToWorld(u.position.copy(tmp.loc))
      u.scale.setScalar(scala)
      u.quaternion.identity()
      u.visible = true
      metaSu.current.position.set(0, 0, 0); metaSu.current.rotation.set(0, 0, 0)
      metaGiu.current.position.set(0, 0, 0)
      biglietto.current.visible = false
      if (t >= 0.85) gumballStore.set('vola')
      return
    }

    if (f.nome === 'vola') {
      const k = easeInOut(clamp(t / 0.7, 0, 1))
      m.localToWorld(tmp.a.copy(SUL_LABBRO))
      camera.localToWorld(tmp.b.copy(DAVANTI))
      u.position.lerpVectors(tmp.a, tmp.b, k)
      u.position.y += Math.sin(k * Math.PI) * 0.18
      u.scale.setScalar(THREE.MathUtils.lerp(scala, SCALA_DAVANTI, k))
      u.rotateY(delta * 5)
      if (t >= 0.75) gumballStore.set('apre')
      return
    }

    if (f.nome === 'apre') {
      camera.localToWorld(tmp.b.copy(DAVANTI))
      u.position.lerp(tmp.b, 1 - Math.exp(-delta * 12))
      u.scale.setScalar(SCALA_DAVANTI)
      // SI GIRA VERSO CHI GUARDA: cosi' le due meta' si separano in alto e
      // in basso sullo SCHERMO, e il biglietto in mezzo e' di fronte
      camera.getWorldQuaternion(tmp.q)
      u.quaternion.slerp(tmp.q, 1 - Math.exp(-delta * 10))
      const k = easeOut(clamp(t / 0.35, 0, 1))
      metaSu.current.position.y = k * R_PALLINA * 1.1
      metaSu.current.rotation.x = -k * 0.35     // il cappello si ribalta verso chi guarda
      metaGiu.current.position.y = -k * R_PALLINA * 0.75
      // il biglietto si srotola nella fessura
      const b = easeOut(clamp((t - 0.1) / 0.3, 0, 1))
      biglietto.current.visible = b > 0
      biglietto.current.scale.set(0.7 + 0.3 * b, Math.max(b, 0.001), 1)
      return
    }

    // --- FERMA: si richiude tutto, la pallina se ne va rimpicciolendo ---
    // Se si e' appena provato a girarla da bloccata, la manopola STRATTONA:
    // un colpetto nel verso del giro che rimbalza sul fermo e si smorza in
    // mezzo secondo. Si capisce da soli che non gira, senza leggere niente.
    const tb = (performance.now() - manopolaBloccata.da) / 1000
    manopola.current.rotation.z = tb < 0.5
      ? -Math.sin(tb * 38) * 0.1 * Math.exp(-tb * 7)
      : 0
    if (sportello.current.rotation.x < -0.001) sportello.current.rotation.x *= Math.exp(-delta * 10)
    if (u.visible) {
      u.scale.multiplyScalar(Math.exp(-delta * 14))
      if (u.scale.x < 0.05) u.visible = false
    }
  })

  return (
    <>
      <group
        ref={macchina}
        position={[cfg.x, cfg.y, cfg.z]}
        rotation-y={cfg.giro * GRADI}
        scale={cfg.scala}
      >
        <mesh geometry={geo.cromoFisso} material={mat.cromo} />
        <mesh geometry={geo.corpo} material={mat.nero} />
        <mesh ref={manopola} geometry={geo.manopola} material={mat.cromo} position={[0, 0.245, 0.17]} />
        {/* lo sportellino, incernierato al bordo di sopra */}
        <group ref={sportello} position={[0, 0.126, 0.198]}>
          <mesh geometry={geo.sportello} material={mat.cromo} position={[0, -0.026, 0]} />
        </group>

        {/* le palline, un solo disegno per tutte */}
        <group ref={mucchio} position={[0, Y_GLOBO, 0]}>
          <instancedMesh ref={palline} args={[geo.pallina, mat.pallina, posti.length]} />
        </group>

        {/* il globo di vetro, disegnato DOPO le palline (e' trasparente) */}
        <mesh geometry={geo.globo} material={mat.vetro} position={[0, Y_GLOBO, 0]} renderOrder={2} />
        <mesh geometry={geo.riflessi} material={mat.riflesso} position={[0, Y_GLOBO, 0]} renderOrder={3} />
      </group>

      {/* LA PALLINA DEL PREMIO vive in coordinate di MONDO, fuori dal
          gruppo: parte dallo scivolo e finisce davanti alla camera, e
          nessuno dei due e' figlio dell'altro. Due meta' che si aprono, e
          in mezzo il biglietto. */}
      <group ref={uscita} visible={false}>
        <group ref={metaSu}>
          <mesh geometry={geo.su} material={mat.fuori} />
          <mesh geometry={geo.su} material={mat.dentro} />
        </group>
        <group ref={metaGiu}>
          <mesh geometry={geo.giu} material={mat.fuori} />
          <mesh geometry={geo.giu} material={mat.dentro} />
        </group>
        <mesh ref={biglietto} geometry={geo.biglietto} material={carta.materiale}
          position={[0, R_PALLINA * 0.17, 0]} visible={false} />
      </group>
    </>
  )
}
